import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, FilmSlate, HandCoins, CalendarBlank, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLORS = {
  Filming: "var(--coral)", Editing: "var(--glow)", Scripting: "#F5A623", Published: "var(--success)",
  Pending: "#F5A623", Invoiced: "var(--glow)", Paid: "var(--success)",
};
const PROJECT_STATUSES = ["Scripting", "Filming", "Editing", "Published"];
const SPONSOR_STATUSES = ["Pending", "Invoiced", "Paid"];

const inputCls = "w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]";
const Pill = ({ status }) => (
  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `color-mix(in srgb, ${STATUS_COLORS[status]} 15%, transparent)`, color: STATUS_COLORS[status] }}>
    {status}
  </span>
);

export default function Dashboard() {
  useSeo("Creator Dashboard", "Track your current projects, sponsorship pipeline and filming calendar in one place.");
  const [projects, setProjects] = useState([]);
  const [sponsors, setSponsors] = useState({ items: [], outstandingTotal: 0 });
  const [events, setEvents] = useState([]);
  const [openProject, setOpenProject] = useState(false);
  const [openSponsor, setOpenSponsor] = useState(false);
  const [pForm, setPForm] = useState({ title: "", status: "Scripting", progress: 20, dueDate: "" });
  const [sForm, setSForm] = useState({ brand: "", amount: "", status: "Pending", dueDate: "" });

  const load = useCallback(async () => {
    const [p, s, c] = await Promise.all([api.get("/projects"), api.get("/sponsors"), api.get("/calendar")]);
    setProjects(p.data);
    setSponsors(s.data);
    setEvents(c.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const addProject = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", { ...pForm, progress: Number(pForm.progress), dueDate: pForm.dueDate || null });
      setOpenProject(false);
      setPForm({ title: "", status: "Scripting", progress: 20, dueDate: "" });
      load();
      toast.success("Project added");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const addSponsor = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sponsors", { ...sForm, amount: Number(sForm.amount) || 0, dueDate: sForm.dueDate || null });
      setOpenSponsor(false);
      setSForm({ brand: "", amount: "", status: "Pending", dueDate: "" });
      load();
      toast.success("Sponsor added");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const cycleSponsorStatus = async (sp) => {
    const next = SPONSOR_STATUSES[(SPONSOR_STATUSES.indexOf(sp.status) + 1) % 3];
    await api.put(`/sponsors/${sp.id}`, { brand: sp.brand, amount: sp.amount, currency: sp.currency, status: next, dueDate: sp.dueDate });
    load();
  };

  const cycleProjectStatus = async (p) => {
    const idx = PROJECT_STATUSES.indexOf(p.status);
    const next = PROJECT_STATUSES[(idx + 1) % 4];
    await api.put(`/projects/${p.id}`, { title: p.title, status: next, progress: next === "Published" ? 100 : p.progress, dueDate: p.dueDate, thumbnailUrl: p.thumbnailUrl });
    load();
  };

  const delItem = async (kind, id) => {
    await api.delete(`/${kind}/${id}`);
    load();
  };

  const upcoming = [...events].sort((a, b) => a.date.localeCompare(b.date)).filter((e) => dayjs(e.date).isAfter(dayjs().subtract(1, "day"))).slice(0, 8);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10 lg:py-14" data-testid="dashboard-page">
      <div className="fade-up mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">Creator HQ</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Dashboard</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Current Projects */}
        <section className="surface rounded-[20px] p-6 fade-up-1" data-testid="projects-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold flex items-center gap-2"><FilmSlate size={20} color="var(--coral)" weight="duotone" /> Current Projects</h2>
            <Dialog open={openProject} onOpenChange={setOpenProject}>
              <DialogTrigger asChild>
                <button data-testid="add-project-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--glow)] text-white text-xs font-semibold hover:bg-[#6a4ce6] transition-colors">
                  <Plus size={13} weight="bold" /> Add
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--card)] border-[var(--border-c)]">
                <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
                <form onSubmit={addProject} className="space-y-4">
                  <input data-testid="project-title-input" required value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} placeholder="Video title" className={inputCls} />
                  <div className="grid grid-cols-2 gap-3">
                    <select data-testid="project-status-select" value={pForm.status} onChange={(e) => setPForm({ ...pForm, status: e.target.value })} className={inputCls}>
                      {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <input data-testid="project-due-input" type="date" value={pForm.dueDate} onChange={(e) => setPForm({ ...pForm, dueDate: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--muted)]">Progress: {pForm.progress}%</label>
                    <input data-testid="project-progress-input" type="range" min="0" max="100" value={pForm.progress} onChange={(e) => setPForm({ ...pForm, progress: e.target.value })} className="w-full accent-[#7C5CFF]" />
                  </div>
                  <button data-testid="project-save-btn" className="w-full py-2.5 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm">Save project</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {projects.length === 0 && <p className="text-[var(--muted)] text-sm py-6" data-testid="projects-empty">No projects yet. Add your first video project.</p>}
          <div className="divide-y divide-[var(--border-c)]">
            {projects.map((p) => (
              <div key={p.id} className="py-4 group" data-testid={`project-row-${p.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-sm truncate">{p.title}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`project-status-${p.id}`} onClick={() => cycleProjectStatus(p)} title="Click to advance status"><Pill status={p.status} /></button>
                    <button data-testid={`project-delete-${p.id}`} onClick={() => delItem("projects", p.id)} className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity"><Trash size={15} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2.5">
                  <div className="flex-1 h-1.5 rounded-full bg-[var(--bg)] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: STATUS_COLORS[p.status] }} />
                  </div>
                  <span className="text-xs text-[var(--muted)] w-9 text-right">{p.progress}%</span>
                </div>
                {p.dueDate && <p className="text-xs text-[var(--muted)] mt-1.5">Due {dayjs(p.dueDate).format("MMM D")}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Sponsorship Pipeline */}
        <section className="surface rounded-[20px] p-6 fade-up-2" data-testid="sponsors-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold flex items-center gap-2"><HandCoins size={20} color="var(--success)" weight="duotone" /> Sponsorship Pipeline</h2>
            <Dialog open={openSponsor} onOpenChange={setOpenSponsor}>
              <DialogTrigger asChild>
                <button data-testid="add-sponsor-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--glow)] text-white text-xs font-semibold hover:bg-[#6a4ce6] transition-colors">
                  <Plus size={13} weight="bold" /> Add
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--card)] border-[var(--border-c)]">
                <DialogHeader><DialogTitle>New sponsorship</DialogTitle></DialogHeader>
                <form onSubmit={addSponsor} className="space-y-4">
                  <input data-testid="sponsor-brand-input" required value={sForm.brand} onChange={(e) => setSForm({ ...sForm, brand: e.target.value })} placeholder="Brand name" className={inputCls} />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="sponsor-amount-input" required type="number" min="0" step="0.01" value={sForm.amount} onChange={(e) => setSForm({ ...sForm, amount: e.target.value })} placeholder="Amount (USD)" className={inputCls} />
                    <select data-testid="sponsor-status-select" value={sForm.status} onChange={(e) => setSForm({ ...sForm, status: e.target.value })} className={inputCls}>
                      {SPONSOR_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <input data-testid="sponsor-due-input" type="date" value={sForm.dueDate} onChange={(e) => setSForm({ ...sForm, dueDate: e.target.value })} className={inputCls} />
                  <button data-testid="sponsor-save-btn" className="w-full py-2.5 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm">Save sponsorship</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4 mb-5">
            <p className="text-xs uppercase tracking-[0.15em] text-[var(--muted)]">Outstanding</p>
            <p className="font-display font-black text-3xl text-[var(--success)] mt-1" data-testid="outstanding-total">
              ${sponsors.outstandingTotal.toLocaleString()}
            </p>
          </div>
          {sponsors.items.length === 0 && <p className="text-[var(--muted)] text-sm py-2" data-testid="sponsors-empty">No sponsorships tracked yet.</p>}
          <div className="divide-y divide-[var(--border-c)]">
            {sponsors.items.map((s) => (
              <div key={s.id} className="py-3.5 flex items-center justify-between gap-3 group" data-testid={`sponsor-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{s.brand}</p>
                  {s.dueDate && <p className="text-xs text-[var(--muted)]">Due {dayjs(s.dueDate).format("MMM D")}</p>}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <span className="font-semibold text-sm">${Number(s.amount).toLocaleString()}</span>
                  <button data-testid={`sponsor-status-${s.id}`} onClick={() => cycleSponsorStatus(s)} title="Click to advance status"><Pill status={s.status} /></button>
                  <button data-testid={`sponsor-delete-${s.id}`} onClick={() => delItem("sponsors", s.id)} className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity"><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Calendar strip */}
      <section className="surface rounded-[20px] p-6 mt-6 fade-up-3" data-testid="calendar-strip">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2"><CalendarBlank size={20} color="var(--glow)" weight="duotone" /> Upcoming</h2>
          <Link to="/calendar" data-testid="calendar-strip-link" className="text-xs font-semibold text-[var(--glow)] hover:underline">Open calendar →</Link>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-[var(--muted)] text-sm">Nothing scheduled. Plan your next film or publish date from the calendar.</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {upcoming.map((e) => (
              <div key={e.id} className="shrink-0 w-40 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4">
                <p className="text-xs font-bold" style={{ color: e.type === "Film" ? "var(--coral)" : "var(--success)" }}>{e.type}</p>
                <p className="font-semibold text-sm mt-1 truncate">{e.title}</p>
                <p className="text-xs text-[var(--muted)] mt-1">{dayjs(e.date).format("ddd, MMM D")}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
